/**
 * @vesper/shared — Barrel Export
 *
 * All types, services, stores, hooks, and constants used by both
 * the web frontend and mobile app.
 */

// ── Storage ───────────────────────────────────────────────────────────────────
export { initStorage, getStorage, setOnAuthFailure, resolveStorageValue } from './storage/storage';
export type { IStorage } from './storage/storage';

// ── Constants ─────────────────────────────────────────────────────────────────
export { initApiBase, getApiBase, API } from './constants/api';
export {
  APP, AGE_PREFERENCE, GENDER_OPTIONS, INTEREST_OPTIONS, HOBBY_OPTIONS,
  ACTIVITY_TYPE_OPTIONS, ATMOSPHERE_OPTIONS, MATCHABLE_TAGS,
  VESPER_SECTOR_TAGS, VESPER_LOOKING_FOR_OPTIONS,
  VESPER_PERSONAL_INTERESTS, VESPER_PERSONAL_INTERESTS_MAX,
  VESPER_PERSONAL_INTEREST_WRITE_IN_MAX_LEN,
  VESPER_STAGE_OPTIONS, VESPER_LOOKING_FOR_VALUES, VESPER_ROLE_OPTIONS,
  SEEDER_PRESETS,
} from './constants/app';
export type { SeederPreset } from './constants/app';

// ── Types ─────────────────────────────────────────────────────────────────────
export type {
  GeoPoint, Gender, AtmosphereType,
  UserVerificationStatus, PrivacySettings, UserPreferences,
  VesperRole, VesperStage, VesperLookingFor,
  UserPublicProfile, AuthResponse, LoginPayload, RegisterPayload,
  CompleteProfilePayload, UpdateProfilePayload, UpdatePrivacyPayload,
  UserTrustScore, UserReliability, UserEventHistoryItem,
  MutualFriendProfile, NearbyUser,
} from './types/user.types';

export type {
  EventStatus, MemberStatus, Event, EventMember, EventWithMembers,
  EnrichedEventMember, EventDetailResponse, CreateEventPayload,
} from './types/event.types';

export type {
  ActivityType, EventPromptStatus, CandidateRole, ScreeningStatus,
  EventPrompt, EventPromptCandidate, EventPromptWithCandidates,
  GroupFormationSummary, CreateEventPromptPayload,
  MatchIntent,
} from './types/event_prompt.types';
export { ACTIVITY_LABELS } from './types/event_prompt.types';

export type {
  ArkChatMessage, ArkChatRequest, ArkChatResponse,
  YelpVenue, TicketmasterEvent, ExternalSearchResult, EventPlanPayload,
  PlaceSuggestion, SkillPreference,
  ExploreResult, ExploreResponse,
  ExploreInitialResponse, SavedEventSummary,
} from './types/ark.types';

export type { EventMessage, EventConversation } from './types/message.types';

export type {
  DMConversation, DMMessage, DMConversationMember,
} from './types/dm.types';

export type {
  NotificationType, Notification, NotificationAction,
  NotificationsResponse, UnreadCountResponse,
} from './types/notification.types';

export type {
  RightNowVisibility, RightNowPost, CreateRightNowPayload,
} from './types/right_now.types';

export type {
  VenueCategory, VenueApprovalStatus, VenueStaffRole,
  VenuePublic, VenueStaff, VenueAccess,
  RegisterVenuePayload, UpdateVenuePayload,
} from './types/venue.types';

export type {
  PublicEventStatus, PublicEventVisibility,
  PublicEventMapPin, PublicEventDetail,
  PlaceSearchResult, CreatePublicEventPayload, UpdatePublicEventPayload,
  OrgMembershipStatus, OrgMembership, OrgPublicProfile, OrgSettings, EventAttendee,
  EventSourceSearchParams, ExternalEventResult, SeederCreateResult, SeederPresetId,
} from './types/public_event.types';

// ── Services ──────────────────────────────────────────────────────────────────
export * as userService from './services/user.service';
export type { TrustScoreLogEntry, TrustScoreEventType, TrustScoreReason, RegistrationStatus } from './services/user.service';
export * as eventService from './services/event.service';
export type { AttendanceStatus } from './services/event.service';
export * as eventPromptService from './services/event_prompt.service';
export type { BenchFriendForPrompt } from './services/event_prompt.service';
export * as cleoService from './services/cleo.service';
export * as messageService from './services/message.service';
export * as notificationService from './services/notification.service';
export * as rightNowService from './services/right_now.service';
export * as geoService from './services/geo.service';
export * as venueService from './services/venue.service';
export type { OrganizerNotice } from './services/venue.service';
export * as publicEventService from './services/public_event.service';
export * as orgService from './services/org.service';
export * as seederService from './services/seeder.service';
export * as exploreService from './services/explore.service';
export * as dmService from './services/dm.service';
export * as forYouService from './services/forYou.service';
export type { ForYouPick, ForYouResponse, ForYouSignal, ForYouEventPick, ForYouEventResponse } from './services/forYou.service';
export * as updateService from './services/update.service';
export type {
  UpdateRecord, UpdateFeedItem, CreateUpdatePayload,
  UpdatePostType, UpdatePosterTone, UpdateStageTag,
} from './services/update.service';
export * as eventShowcaseService from './services/eventShowcase.service';
export type { ShowcaseEligibility } from './services/eventShowcase.service';
export * as cleoIntrosService from './services/cleoIntros.service';
export type { CleoIntroRecord, CleoIntroStatus } from './services/cleoIntros.service';
export * as blockService from './services/block.service';
export type { BlockedRow } from './services/block.service';
export * as demoService from './services/demo.service';
export type { DemoArchetype, StartDemoPayload, StartDemoResult, HeartbeatResult, DemoSpoofNote } from './services/demo.service';
export { deduped, bust, bustByPrefix, bustAll } from './services/dedupedFetch';
export type { ReverseGeocodeResult, NearbyAllResult, RoomMapPin, DemoCityCluster } from './services/geo.service';
export type { RatingSessionPayload, BenchMember, FriendRequest, ColdConnectQuota } from './services/cleo.service';

// ── Stores ────────────────────────────────────────────────────────────────────
export { useAuthStore } from './store/userSlice';
export { useEventStore } from './store/eventSlice';

// ── Hooks ─────────────────────────────────────────────────────────────────────
export { useProfile } from './hooks/useProfile';
export type { UseProfileReturn } from './hooks/useProfile';
export { useCleo } from './hooks/useCleo';
export { useExplore } from './hooks/useExplore';
export { useDMChat } from './hooks/useDMChat';
export type { UseDMChatReturn, SocketLike } from './hooks/useDMChat';
